import React, { forwardRef } from 'react'
import { motion } from 'framer-motion'

interface SlidingToggleProps {
  options: {
    value: string
    label: string
    icon: React.ReactNode
  }[]
  value: string
  onChange: (value: string) => void
  className?: string
  textSize?: 'xs' | 'sm' | 'base'
}

const SlidingToggle = forwardRef<HTMLDivElement, SlidingToggleProps>((
  { 
    options, 
    value, 
    onChange, 
    className = '',
    textSize = 'sm'
  },
  ref
) => {
  const selectedIndex = options.findIndex(option => option.value === value)
  
  const getTextSizeClass = () => {
    switch (textSize) {
      case 'xs': return 'text-xs'
      case 'sm': return 'text-sm'
      case 'base': return 'text-base'
      default: return 'text-sm'
    }
  }

  return (
    <div ref={ref} className={`relative flex p-0.5 bg-gray-100 dark:bg-gray-700 rounded-full border border-gray-300 dark:border-gray-600 ${className}`}>
      {/* Sliding pill */}
      <motion.div
        className="absolute inset-y-0.5 bg-white dark:bg-gray-800 rounded-full shadow-md border border-orange-300 dark:border-orange-700"
        initial={false}
        animate={{
          left: `${(selectedIndex / options.length) * 100}%`,
          width: `${(1 / options.length) * 100}%`,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
        }}
      />
      
      {/* Options */}
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-full ${getTextSizeClass()} font-medium transition-colors duration-200 min-w-0 ${
            value === option.value
              ? 'text-orange-600 dark:text-orange-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          <motion.div
            animate={{
              scale: value === option.value ? 1.05 : 1,
            }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 25,
            }}
            className="flex items-center justify-center"
          >
            {option.icon}
          </motion.div>
          <span className="truncate">{option.label}</span>
        </button>
      ))}
    </div>
  )
})

SlidingToggle.displayName = 'SlidingToggle'

export default SlidingToggle 